import { useState } from "react";
import {
  Coffee,
  ShoppingBag,
  Sparkles,
  Wrench,
  Building2,
  ArrowRight,
  ChevronDown,
  Quote,
  type LucideIcon,
} from "lucide-react";
import { useLandingContent, type BusinessTypeItem } from "@/features/admin/content/landingContent";

const ICONS: Record<BusinessTypeItem["iconKey"], LucideIcon> = {
  coffee: Coffee,
  shopping: ShoppingBag,
  sparkles: Sparkles,
  wrench: Wrench,
  building: Building2,
};

/**
 * Tipos de negocio — editable desde el admin. Cada categoría tiene: icono,
 * título, tagline, descripción, etiquetas, desplegables (FAQs), imagen, enlace y
 * un testimonio que se muestra debajo de la imagen.
 */
export function BusinessTypes() {
  const { businessTypes } = useLandingContent();
  const [active, setActive] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);
  if (!businessTypes.length) return null;
  const parent = businessTypes[Math.min(active, businessTypes.length - 1)];
  const Icon = ICONS[parent.iconKey] ?? Building2;

  return (
    <section id="tipos" className="py-24 lg:py-32 bg-neutral-50 relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_50%_at_20%_30%,rgba(217,96,46,0.08),transparent_70%)]" />
      <div className="max-w-7xl mx-auto px-5 lg:px-6">
        <div className="mb-12 lg:mb-16 max-w-2xl reveal">
          <span className="text-xs font-bold uppercase tracking-widest text-primary">Tipos de negocio</span>
          <h2 className="text-3xl lg:text-5xl font-bold tracking-tight mt-3">Diseñado para cada tipo de negocio.</h2>
          <p className="text-muted-foreground mt-4 text-base lg:text-lg">
            Elige tu categoría y descubre cómo TecoPOS se adapta a tu día a día.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 reveal">
          {/* Selector de categorías: rejilla en móvil (todo visible, sin scroll
              horizontal), columna vertical en desktop. */}
          <aside className="lg:col-span-3">
            <ul className="grid grid-cols-2 sm:grid-cols-3 lg:flex lg:flex-col gap-2">
              {businessTypes.map((p, i) => {
                const PIcon = ICONS[p.iconKey] ?? Building2;
                const isActive = i === active;
                return (
                  <li key={p.id}>
                    <button
                      onClick={() => { setActive(i); setOpenFaq(0); }}
                      aria-pressed={isActive}
                      className={`group w-full h-full text-left flex items-center gap-2.5 lg:gap-3 px-3 py-3 lg:px-4 lg:py-3.5 rounded-2xl transition-all border ${
                        isActive ? "bg-foreground text-background border-foreground shadow-lg" : "bg-white border-black/5 hover:border-primary/30"
                      }`}
                    >
                      <span className={`size-8 lg:size-9 shrink-0 rounded-xl grid place-items-center transition-colors ${isActive ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"}`}>
                        {p.iconSvg ? <img src={p.iconSvg} alt="" className="size-4 object-contain" /> : <PIcon className="size-4" />}
                      </span>
                      <span className="text-[13px] lg:text-sm font-semibold leading-tight">{p.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* Contenido central */}
          <div className="lg:col-span-6 space-y-6">
            <div key={parent.id} className="animate-fade-up bg-white rounded-3xl border border-black/5 p-6 lg:p-8 shadow-sm">
              <span className="text-[11px] font-bold uppercase tracking-widest text-primary">{parent.tagline}</span>
              <h3 className="text-2xl lg:text-3xl font-bold tracking-tight mt-2 mb-3 flex items-center gap-2">
                {parent.iconSvg ? <img src={parent.iconSvg} alt="" className="size-6 object-contain" /> : <Icon className="size-6 text-primary" />}
                {parent.label}
              </h3>
              <p className="text-muted-foreground mb-5">{parent.description}</p>
              {parent.children.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {parent.children.map((c) => (
                    <span key={c} className="px-3 py-1.5 rounded-full bg-neutral-100 text-xs font-semibold text-foreground hover:bg-primary hover:text-primary-foreground transition-colors cursor-pointer">
                      {c}
                    </span>
                  ))}
                </div>
              )}

              {/* Desplegables (FAQs) */}
              <div className="space-y-2">
                {parent.faqs.map((f, i) => {
                  const open = openFaq === i;
                  return (
                    <div key={`${parent.id}-${i}`} className={`rounded-2xl border transition-all ${open ? "border-primary/30 bg-primary/5" : "border-black/5 bg-white"}`}>
                      <button onClick={() => setOpenFaq(open ? -1 : i)} className="w-full flex items-center justify-between gap-4 p-4 lg:p-5 text-left">
                        <span className="font-semibold text-sm lg:text-base">{f.q}</span>
                        <ChevronDown className={`size-4 shrink-0 transition-transform ${open ? "rotate-180 text-primary" : ""}`} />
                      </button>
                      <div className={`grid transition-all duration-500 ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                        <div className="overflow-hidden">
                          <p className="px-4 lg:px-5 pb-5 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <a href={parent.link || "/registro"} className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all">
                Ver solución completa
                <ArrowRight className="size-3.5" />
              </a>
            </div>
          </div>

          {/* Banner + testimonio debajo */}
          <div className="grid gap-5 sm:grid-cols-2 lg:col-span-3 lg:grid-cols-1">
            <a href={parent.link || "/registro"} key={parent.id + "b"} className="animate-fade-up block relative rounded-3xl overflow-hidden aspect-[16/10] lg:aspect-[4/5] border border-black/5 shadow-lg group">
              {parent.banner ? (
                <>
                  {/* Fondo borroso de relleno: solo visible en desktop, donde la
                      imagen va contenida (object-contain) y deja franjas. En móvil
                      la imagen recorta a formato pequeño (object-cover) y lo tapa. */}
                  <img
                    aria-hidden="true"
                    src={parent.banner}
                    className="absolute inset-0 h-full w-full scale-110 object-cover object-center blur-2xl brightness-[0.6]"
                  />
                  <img
                    src={parent.banner}
                    alt={parent.label}
                    className="relative h-full w-full object-cover lg:object-contain object-center group-hover:scale-105 transition-transform duration-500"
                  />
                </>
              ) : (
                <div className="h-full w-full grid place-items-center bg-neutral-100 text-neutral-400 text-sm">sin imagen</div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                <p className="text-sm font-bold">{parent.label}</p>
                <p className="text-xs text-white/80">{parent.tagline}</p>
              </div>
            </a>

            {parent.testimonial?.quote && (
              <div className="animate-fade-up rounded-3xl border border-black/5 bg-white p-5 shadow-sm">
                <Quote className="size-5 text-primary/30" />
                <p className="mt-2 text-sm leading-relaxed text-foreground">"{parent.testimonial.quote}"</p>
                <div className="mt-3">
                  <p className="text-sm font-semibold">{parent.testimonial.name}</p>
                  {parent.testimonial.role && <p className="text-xs text-muted-foreground">{parent.testimonial.role}</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
